import type { Container } from "pixi.js";
import { GameScene } from "../scene/GameScene";
import { BonusTransition } from "../views/game-transition/BonusTransition";
import type { CoinHoldHook } from "./CoinHoldHook";
import { installFpsMeter } from "./installFpsMeter";

/**
 * Adds coin-hold's members to the shell's hook: the scene (and the bonus transition, over the panels) is found on the stage
 * and read through the readout the round presentation set; a node's drawn alpha is read up its ancestors (the shell's `isShown` reads `visible` only). With
 * `?fps=1`, a frame-rate readout for trying the game on a phone.
 */
export function installCoinHoldHook(input: { hook: object; stage: Container }): void {
  // A frame-rate readout for a phone (`?fps=1`).
  installFpsMeter(window.location.search);
  const scene = find(input.stage, GameScene);
  const transition = find(input.stage, BonusTransition);
  const members: CoinHoldHook = {
    getBoard: () => scene?.readout?.board() ?? [],
    getPots: () => ({ ...(scene?.readout?.pots() ?? { life: 0, multi: 0, grow: 0 }) }),
    getMultipliers: () => Object.fromEntries(scene?.readout?.multipliers() ?? []),
    isGrown: () => scene?.isGrown ?? false,
    getPotion: () => ({ ...(scene?.pots.potion ?? { loaded: false, life: 0, multi: 0, grow: 0 }) }),
    getLights: () => ({ loaded: scene?.fx.lights.ready ?? false, ...(scene?.fx.lights.inFlight ?? { life: 0, multi: 0, grow: 0 }) }),
    getImpacts: () => {
      const state = scene?.fx.impacts.state ?? { played: { life: 0, multi: 0, grow: 0 }, playing: { life: false, multi: false, grow: false } };
      return { loaded: scene?.fx.impacts.ready ?? false, hits: state.played, playing: state.playing };
    },
    getEruptions: () => {
      const state = scene?.fx.eruptions.state ?? { played: { life: 0, multi: 0, grow: 0 }, playing: { life: false, multi: false, grow: false } };
      return { loaded: scene?.fx.eruptions.ready ?? false, erupted: state.played, playing: state.playing };
    },
    getStreaks: () => {
      const none = { life: 0, multi: 0, grow: 0 };
      return { loaded: scene?.fx.streaks.ready ?? false, ...(scene?.fx.streaks.state ?? { falling: none, landing: none, landed: none, passed: none }) };
    },
    getEdgeFire: () => ({ ...(scene?.edgeFire.state ?? { loaded: false, on: false, ignited: 0, drawn: false }) }),
    getCellFire: () => ({
      loaded: (scene?.fx.cellHits.ready ?? false) && (scene?.cellFires.ready ?? false),
      hits: scene?.fx.cellHits.state.hits ?? 0,
      burning: scene?.cellFires.state.burning ?? [],
    }),
    getBoardImpact: () => ({ ...(scene?.fx.boardImpact.state ?? { loaded: false, hits: 0, playing: false }) }),
    getBonusTransition: () => ({ ...(transition?.state ?? { loaded: false, plays: 0, playing: false }) }),
    getCoinBursts: () => scene?.coinBursts.state ?? { thrown: { life: 0, multi: 0, grow: 0 }, flying: 0 },
    getCollectors: () => scene?.collectors.state ?? { loaded: false, lent: 0, activations: 0, pulses: 0 },
    getMysteryReel: () => scene?.fx.mysteryReel.state ?? { loaded: false, spins: 0, landed: [] },
    getCoinShine: () => scene?.coinShines.state ?? { loaded: false, coins: 0, drawn: 0 },
    getPotShine: () => ({ ...(scene?.pots.shine.state ?? { loaded: false, drawn: 0 }) }),
    getSmoke: () => ({ ...(scene?.pots.smoke.state ?? { loaded: false, life: false, multi: false, grow: false }) }),
    getRespinMeter: () => ({ shown: scene?.meter.visible ?? false, count: scene?.meter.shown ?? 0, flames: scene?.meter.flamesBurning ?? false }),
    getLineSelector: () => ({ shown: scene?.lineSelector.visible ?? false, lines: scene?.lineSelector.shownLines ?? 0, ...(scene?.lineSelector.arrows ?? { less: false, more: false }) }),
    getLitRows: () => [...(scene?.readout?.litRows() ?? [])],
    getLitBlanks: () => [...(scene?.readout?.litBlanks() ?? [])],
    getPaidCells: () => [...(scene?.readout?.paid() ?? [])],
    getShine: () => {
      const shine = scene?.readout?.shine();
      return { glinting: [...(shine?.glinting ?? [])], moments: [...(shine?.moments ?? [])], sparkles: shine?.sparkles ?? 0 };
    },
    getDrawnAlpha: (label) => {
      let node = findShown(input.stage, label);
      let alpha = node ? 1 : 0;
      for (; node; node = node.parent) alpha *= node.alpha;
      return alpha;
    },
  };
  Object.assign(input.hook, members);
}

/** The first node labelled `label` inside a visible subtree. */
function findShown(root: Container, label: string): Container | null {
  if (!root.visible) return null;
  if (root.label === label) return root;
  for (const child of root.children) {
    const hit = findShown(child, label);
    if (hit) return hit;
  }
  return null;
}

/** The first node of `type` in the tree. */
function find<T>(node: Container, type: abstract new (...args: never[]) => T): T | null {
  if (node instanceof type) return node;
  for (const child of node.children) {
    const found = find(child, type);
    if (found) return found;
  }
  return null;
}
